"use client";

/**
 * EPIC-053 Market Creation Hub — live countdown timer for markets
 * with a future `closes_at`. Reference: Polymarket BTC May 11-17
 * "04 DAYS 10 HRS 42 MINS" headline above the price chart.
 *
 * Updates every second via `setInterval` (cleared on unmount). Unit
 * granularity shifts as zero approaches so the user always sees the
 * three most-relevant units:
 *
 *   - >24h remaining → DAYS / HRS / MINS
 *   - 1h–24h        → HRS / MINS / SECS
 *   - <1h           → MINS / SECS (two-unit display)
 *   - <=0           → "Closed", `onZero` fires exactly once
 *
 * SSR-safe: initial render computes the delta from `endsAt` on the
 * server (UTC math, no Date.now() drift between server + client).
 * Client useEffect re-syncs after hydration to avoid the second-of-
 * delay flash that would otherwise show stale "01 SECS" on first
 * paint after a long server→client gap.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  endsAt: string | Date;
  /**
   * `verbose` matches the Polymarket reference shape
   * "04 DAYS  10 HRS  42 MINS" with full unit labels. `compact` is
   * the card-pill shape "04D 10H 42M" for tight slots (market index
   * cards, header strips, mobile bottom sheet).
   */
  format?: "compact" | "verbose";
  /**
   * Fires exactly once when the countdown crosses zero. Useful for
   * the parent surface to trigger a refetch (e.g. flip the trade
   * panel into "trading closed" mode) without depending on a
   * separate WS event.
   */
  onZero?: () => void;
  classNameContainer?: string;
  classNameDigit?: string;
  classNameLabel?: string;
}

interface Delta {
  totalMs: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function computeDelta(endsAt: string | Date, now: number): Delta {
  const target =
    endsAt instanceof Date ? endsAt.getTime() : new Date(endsAt).getTime();
  if (!Number.isFinite(target)) {
    return { totalMs: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  const diff = Math.max(0, target - now);
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { totalMs: diff, days, hours, minutes, seconds };
}

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

export function LiveCountdown({
  endsAt,
  format = "verbose",
  onZero,
  classNameContainer,
  classNameDigit,
  classNameLabel,
}: Props) {
  const { t } = useTranslation();

  // Initial state uses Date.now() at module-render time. On the
  // server this is the server's clock at request time; on the
  // client the post-hydration useEffect re-syncs to the client
  // clock + starts the 1s tick. SSR-rendered HTML may show a
  // 1-2 second stale value before hydration, which is acceptable
  // (and identical to what Polymarket / Kalshi ship).
  const [delta, setDelta] = useState<Delta>(() =>
    computeDelta(endsAt, Date.now()),
  );

  const onZeroFiredRef = useRef(false);

  useEffect(() => {
    // Reset onZero gate when endsAt changes (e.g. admin edits the
    // close time on a market). Without this an extended countdown
    // would never fire onZero a second time.
    onZeroFiredRef.current = false;

    // Re-sync immediately on mount / endsAt change.
    setDelta(computeDelta(endsAt, Date.now()));

    const id = setInterval(() => {
      const next = computeDelta(endsAt, Date.now());
      setDelta(next);
      if (next.totalMs <= 0 && !onZeroFiredRef.current) {
        onZeroFiredRef.current = true;
        onZero?.();
      }
    }, 1000);

    return () => clearInterval(id);
  }, [endsAt, onZero]);

  if (delta.totalMs <= 0) {
    return (
      <span
        aria-live="polite"
        aria-label={t("liveCountdown.closedAria", "Closed")}
        className={classNameContainer}
      >
        {t("liveCountdown.closed", "Closed")}
      </span>
    );
  }

  // Pick units based on remaining time.
  type Unit = { value: number; longLabel: string; shortLabel: string };
  const units: Unit[] = [];
  const moreThan24h = delta.days > 0;
  const lessThan1h = delta.days === 0 && delta.hours === 0;

  if (moreThan24h) {
    units.push({
      value: delta.days,
      longLabel: t("liveCountdown.daysLong", "DAYS"),
      shortLabel: t("liveCountdown.daysShort", "D"),
    });
    units.push({
      value: delta.hours,
      longLabel: t("liveCountdown.hoursLong", "HRS"),
      shortLabel: t("liveCountdown.hoursShort", "H"),
    });
    units.push({
      value: delta.minutes,
      longLabel: t("liveCountdown.minutesLong", "MINS"),
      shortLabel: t("liveCountdown.minutesShort", "M"),
    });
  } else if (!lessThan1h) {
    // 1h–24h window: HRS / MINS / SECS.
    units.push({
      value: delta.hours,
      longLabel: t("liveCountdown.hoursLong", "HRS"),
      shortLabel: t("liveCountdown.hoursShort", "H"),
    });
    units.push({
      value: delta.minutes,
      longLabel: t("liveCountdown.minutesLong", "MINS"),
      shortLabel: t("liveCountdown.minutesShort", "M"),
    });
    units.push({
      value: delta.seconds,
      longLabel: t("liveCountdown.secondsLong", "SECS"),
      shortLabel: t("liveCountdown.secondsShort", "S"),
    });
  } else {
    // <1h: MINS / SECS only (two-unit display).
    units.push({
      value: delta.minutes,
      longLabel: t("liveCountdown.minutesLong", "MINS"),
      shortLabel: t("liveCountdown.minutesShort", "M"),
    });
    units.push({
      value: delta.seconds,
      longLabel: t("liveCountdown.secondsLong", "SECS"),
      shortLabel: t("liveCountdown.secondsShort", "S"),
    });
  }

  // ARIA label uses verbose form regardless of display format so
  // screen readers always get the full unit names. Updates on each
  // tick — aria-live=polite throttles the announcement cadence so
  // we don't spam every second.
  const ariaLabel = t("liveCountdown.aria", {
    defaultValue: "closes in {{val}}",
    val: units.map((u) => `${u.value} ${u.longLabel}`).join(" "),
  });

  if (format === "compact") {
    return (
      <span
        aria-live="polite"
        aria-label={ariaLabel}
        className={
          classNameContainer ??
          "inline-flex items-center gap-1 text-[11px] font-bold tabular-nums text-[var(--color-text2)]"
        }
      >
        {units.map((u, i) => (
          <span key={u.shortLabel}>
            {pad2(u.value)}
            {u.shortLabel}
            {i < units.length - 1 ? " " : ""}
          </span>
        ))}
      </span>
    );
  }

  // verbose — paired digit + label blocks separated by spacing.
  return (
    <div
      aria-live="polite"
      aria-label={ariaLabel}
      className={
        classNameContainer ??
        "flex items-baseline gap-4 text-[var(--color-text)]"
      }
    >
      {units.map((u) => (
        <div
          key={u.longLabel}
          className="flex items-baseline gap-1.5 tabular-nums"
        >
          <span
            className={
              classNameDigit ?? "text-4xl font-bold leading-none md:text-5xl"
            }
          >
            {pad2(u.value)}
          </span>
          <span
            className={
              classNameLabel ??
              "text-[11px] font-bold uppercase tracking-[0.5px] text-[var(--color-text3)]"
            }
          >
            {u.longLabel}
          </span>
        </div>
      ))}
    </div>
  );
}
